import {Component, effect, signal, ViewChild} from '@angular/core';
import {Division} from '../../../models/division';
import {TeamsService} from '../../../services/teams.service';
import {DivisionSelectorComponent} from '../../../shared/components/division-selector/division-selector.component';
import {GroupSelectorComponent} from '../../../shared/components/group-selector/group-selector.component';
import {Group} from '../../../models/group';
import {forkJoin, of, switchMap} from 'rxjs';
import {Utils} from '../../../shared/utils';
import {StorageService} from "../../../services/storage.service";
import {Title} from "@angular/platform-browser";
import {NbSpinnerModule, NbTabsetComponent, NbTabsetModule} from "@nebular/theme";
import {OrganizeComponent} from "./organize/organize.component";
import {ScheduledComponent} from "./scheduled/scheduled.component";
import {ReportComponent} from "./report/report.component";
import {PlayedComponent} from "./played/played.component";
import {PlanningComponent} from "./planning/planning.component";
import {TranslateModule} from '@ngx-translate/core';

@Component({
  selector: 'app-games-v2',
  standalone: true,
  imports: [
    TranslateModule,DivisionSelectorComponent, GroupSelectorComponent, NbSpinnerModule, NbTabsetModule, OrganizeComponent, ScheduledComponent, ReportComponent, PlayedComponent, PlanningComponent],
  templateUrl: './games-v2.component.html',
  styleUrl: './games-v2.component.scss'
})
export class GamesV2Component {
  @ViewChild('tabset') tabset!: NbTabsetComponent;

  selectedDivisionId = signal<string>('');
  selectedGroupId: string = 'A';
  selectedTabId: string = 'scheduled';
  isLoggedIn = false;
  loading = false;

  nbNotifications = signal<string>('');
  displayGroupFilter = signal<boolean>(false);
  refreshTabs = signal<string>('');
  showPrivateTabs = signal<boolean>(false);
  divisions = signal<Division[]>([]);
  groups = signal<Group[]>([]);
  selectedDivisionName = signal<string>('');
  selectedGroupName = signal<string>('');
  selectedDivisionIdSignal = signal<string>(this.selectedDivisionId());
  selectedGroupIdSignal = signal<string>(this.selectedGroupId);
  currentUserTeamIds = signal<string[]>([]);
  currentUserDivisionIds = signal<string[]>([]);

  currentTitle!: string;
  divisionsArray: Division[] = []
  groupsArray: Group[] = []

  constructor(
    public utils: Utils,
    private readonly title: Title,
    private storageService: StorageService,
    private teamsService: TeamsService
  ) {
    effect(() => {
      this.showPrivateTabs();
      setTimeout(() => {
        this.selectFirstTab();
      });
    });
  }

  ngOnInit(): void {
    this.currentTitle = this.title.getTitle();
    this.isLoggedIn = this.storageService.isLoggedIn();
    this.selectedTabId = this.isLoggedIn ? 'scheduled' : 'played';
    this.loading = true;

    this.selectedDivisionName.set('');
    this.selectedGroupName.set('');

    const observable = this.teamsService.divisions().pipe(
      switchMap(divisions => {
        const firstDivision = divisions[0];
        return forkJoin({
          divisions: of(divisions),
          groups: this.teamsService.groupsByDivision(firstDivision.division_id),
          currentUserTeamIds: this.isLoggedIn ? this.teamsService.currentUserTeamIds() : of([]),
          currentUserDivisionIds: this.isLoggedIn ? this.teamsService.currentUserDivisionIds() : of([]),
        });
      })
    );

    observable.subscribe((result) => {
      Utils.setFirstDivisionForSignal(this.selectedDivisionId, result.divisions);
      this.divisionsArray = result.divisions;
      this.groupsArray = result.groups;

      const saved = this.storageService.getSelectedDivisionId();
      if (saved && this.selectedDivisionId() !== saved) {
        this.selectedDivisionId.set(saved);
      }

      this.currentUserTeamIds.set(result.currentUserTeamIds);
      this.currentUserDivisionIds.set(result.currentUserDivisionIds);
      this.selectedDivisionName.set(this.divisionsArray.find(d => d.division_id === this.selectedDivisionId())?.name ?? '');
      this.selectedGroupName.set(this.groupsArray.find(d => d.group_id === this.selectedGroupId)?.name ?? '');
      this.divisions.set(result.divisions);
      this.groups.set(result.groups);
      this.selectedDivisionIdSignal.set(this.selectedDivisionId());
      this.selectedGroupIdSignal.set(this.selectedGroupId);
      this.checkShowPrivateTabs();
      this.loading = false;
    });
  }

  onDivisionChange($event: any) {
    this.selectedDivisionId.set($event);

    this.storageService.storeSelectedDivisionId(this.selectedDivisionId());

    if (this.divisionsArray.length > 0) {
      this.selectedDivisionIdSignal.set(this.selectedDivisionId());
      this.selectedGroupIdSignal.set(this.selectedGroupId);
      this.selectedDivisionName.set(this.divisionsArray.find(d => d.division_id === this.selectedDivisionId())?.name ?? '');
      this.selectedGroupName.set(this.groupsArray.find(d => d.group_id === this.selectedGroupId)?.name ?? '');
      this.checkShowPrivateTabs();
      this.teamsService.groupsByDivision(this.selectedDivisionIdSignal()).subscribe((result) => {
        this.groups.set(result);
        this.selectedGroupIdSignal.set('A');
      });
    }
  }

  onGroupChange($event: any) {
    this.selectedGroupId = $event;
    this.selectedGroupIdSignal.set(this.selectedGroupId);
    this.selectedGroupName.set(this.groupsArray.find(d => d.group_id === this.selectedGroupId)?.name ?? '');
  }

  onNbNotificationsChange($event: any) {
    this.nbNotifications.set($event);
  }

  protected onTabChange($event: any) {
    this.selectedTabId = $event.tabId;
    const display = this.selectedTabId != 'planning' && this.selectedTabId != 'report-game';
    this.displayGroupFilter.set(display);
  }

  protected onMatchUpdate(updatedBy: string) {
    this.refreshTabs.set(updatedBy);
  }

  protected checkShowPrivateTabs() {
    this.showPrivateTabs.set(false);
    const result = this.isLoggedIn && this.currentUserDivisionIds().find(value => value === this.selectedDivisionId());
    if (result) {
      this.showPrivateTabs.set(true);
    }
  }

  private selectFirstTab() {
    this.tabset.selectTab(this.tabset.tabs.first);
  }
}
